/* Two registers, two names for the same school.
 *
 * The risk register files against the layer's code, AAS. The permit register
 * files against a name somebody typed, "Alfredo Andrews". Neither is wrong,
 * and the board draws the school twice because it cannot tell they are the
 * same.
 *
 * Matching them by text alone is not safe here. The real pairs include a
 * missing middle initial, a name longer than the layer's own, an abbreviation
 * of an island, and at least one entry that looks like two schools joined. A
 * rule confident enough to catch those is confident enough to attach
 * somebody's permits to the wrong school, and nobody would ever see it.
 *
 * So nothing is matched silently. The app suggests, a person confirms once,
 * and the answer is stored on the contract. What is stored is a person's
 * decision, not a guess.
 */

/* Words that say what kind of place it is rather than which place it is.
   They are the same on half the register and carry no information. */
const NOISE = new Set(["SCHOOL", "SCHOOLS", "THE", "OF", "AND"]);

/* PK8, PreK-8, K-8 and PREK8 are one thing written four ways, and the two
   registers between them use all four. Joined up before the string is split,
   because the hyphen in PreK-8 would otherwise break it into two tokens that
   match nothing. */
const grades = (text) => text.replace(/\b(?:PRE|P)?K[-\s]?(\d+)\b/g, "PREK$1");

export const normalise = (value) =>
  String(value ?? "").trim().toUpperCase().replace(/\s+/g, " ");

export function tokens(value) {
  return grades(normalise(value))
    .split(/[^A-Z0-9]+/)
    .filter(Boolean)
    .filter((t) => !NOISE.has(t));
}

/* How much two names have in common, against the shorter of the two. Against
   the shorter, because "Guy Benjamin Center" and "Guy Benjamin" are the same
   school and neither is a subset failure. */
export function similarity(a, b) {
  const left = new Set(tokens(a));
  const right = new Set(tokens(b));
  if (!left.size || !right.size) return 0;
  let shared = 0;
  for (const t of left) if (right.has(t)) shared += 1;
  return shared / Math.min(left.size, right.size);
}

/* The best school for a value nobody has matched yet, or null. Only ever a
   suggestion to put in front of a person: it is never applied on its own. */
export function suggestMatch(value, schools = [], floor = 0.6) {
  const mine = tokens(value);
  let best = null;
  for (const school of schools) {
    /* A code is matched whole or not at all. Scored the same way as a name it
       would be worthless: a code is one token, so any value containing it
       scores full marks, and "CAHS/LPK" would be confidently filed as CAHS
       with the LPK half quietly lost. */
    const exactCode = mine.length === 1 && mine[0] === normalise(school.code);
    const score = exactCode ? 1 : similarity(value, school.name);
    if (!best || score > best.score) best = { ...school, score };
  }
  return best && best.score >= floor ? best : null;
}

/* Which schools a register's value means, decided in this order: what a
   person said, then the layer's own code, then the layer's own name. Anything
   left is unmatched and is shown rather than guessed at.
 *
 * A list rather than one school, because one entry can genuinely be two. The
 * permit register files some rows against "CAHS/LPK", and those permits belong
 * to Charlotte Amalie and to Lockhart both. Forcing a choice there would put
 * them on one card and lose them from the other, which is a worse answer than
 * saying nothing. */
export function resolveSites(value, { names = new Map(), aliases = {} } = {}) {
  const key = normalise(value);
  if (!key) return [];

  const said = aliases[key];
  if (said) {
    const codes = (Array.isArray(said) ? said : [said]).filter((c) => names.has(c));
    if (codes.length) return codes;
  }

  if (names.has(key)) return [key];

  for (const [code, name] of names) {
    if (normalise(name) === key) return [code];
  }
  return [];
}

/* The single school where there is exactly one, for the places that can only
   hold one answer. */
export const resolveSite = (value, ctx) => resolveSites(value, ctx)[0] ?? null;

/* Every value in the registers that nothing yet accounts for, each with the
   app's best suggestion beside it. */
export function unmatchedSites({ sites = [], names = new Map(), aliases = {} } = {}) {
  const schools = [...names].map(([code, name]) => ({ code, name }));
  const seen = new Set();
  const out = [];
  for (const raw of sites) {
    const key = normalise(raw);
    if (!key || seen.has(key)) continue;
    seen.add(key);
    if (resolveSites(raw, { names, aliases }).length) continue;
    out.push({ value: raw, key, suggestion: suggestMatch(raw, schools) });
  }
  return out.sort((a, b) => a.value.localeCompare(b.value));
}
